import { create } from 'zustand'
import {
  reportsList as seedReports,
  personnelList as seedPersonnel,
  alertsList as seedAlerts,
  usersList as seedUsers,
  auditLogList as seedAudit,
  kpiDataList as seedKpi,
} from './mockData'

// ============================================================================
// DENS CAKRA — Live Application Store (single mutable source of truth)
// Every action mutates shared state AND appends an audit-log entry so that
// any workflow performed on one screen propagates across the whole platform.
// ============================================================================

export type ReportStage =
  | 'Draft'
  | 'Dikirim'
  | 'Review Koordinator'
  | 'Diverifikasi'
  | 'Analisis'
  | 'Disetujui BINDA'
  | 'Perlu Revisi'
  | 'Diterima Nasional'
  | 'Tindak Lanjut'
  | 'Selesai'
  | 'Ditolak'

export interface KpiHistoryEntry {
  time: string
  from: number
  to: number
  delta: number
  reason: string
  target: string
}

export interface TimelineEntry {
  stage: string
  time: string
  actor: string
  note: string
}

export interface Report {
  id: string
  title: string
  type: string
  category: string
  urgency: string
  sender: string
  region: string
  time: string
  status: string
  stage: string
  description?: string
  // Rich intelligence content
  ringkasan?: string
  kronologi?: string
  pihakTerkait?: string
  potensiDampak?: string
  rekomendasiAwal?: string
  // Reporter / location detail
  unit?: string
  binda?: string
  provinsi?: string
  kabupaten?: string
  kecamatan?: string
  // Analyst output
  analysisNote?: string
  intelligenceAssessment?: string
  threatClass?: string
  threatLevel?: string
  impactScore?: number
  rejectReason?: string
  verificationStatus?: string
  followUpStatus?: string
  attachments?: { name: string; kind: 'foto' | 'dokumen' | 'video' | 'audio' }[]
  geotag?: { lat: number; lng: number; label: string }
  offline?: boolean
  // Coordinator stage
  coordinatorNote?: string
  // National escalation flags (surface in National Dashboard / Command Center)
  escalatedNational?: boolean
  rekomendasiEskalasi?: string
  // AI-assisted analyst workbench: structured, editable intelligence report.
  // Generated by the (simulated) AI engine, then edited & approved by the analyst.
  aiReport?: AiIntelReport
  aiGenerated?: boolean
  // National analyst (Level 2) stage
  nationalReviewed?: boolean
  executiveSummary?: string
  nationalAssessment?: string
  strategicAnalysis?: string
  nationalRiskAssessment?: string
  executiveNotes?: string
  revisionReason?: string
  bindaApprovalNote?: string
  timeline?: TimelineEntry[]
}

export function isOfficialProduct(report: Report): boolean {
  return report.status === 'Diterima Nasional'
}

export interface AiIntelReport {
  classification: string
  nomor: string
  tanggal: string
  subject: string
  facts: string
  situationSummary: string
  keyFindings: string[]
  mainActors: string
  riskAnalysis: string
  potentialImpact: string
  earlyWarning: string[]
  recommendations: string
  assessment: string
  authentication: string
  generatedAt: string
}

export interface AlertItem {
  id: string
  type: string
  source: string
  location: string
  time: string
  desc: string
  status: string
  isPanic?: boolean
  reporter?: string
  timeline?: TimelineEntry[]
}

export interface UserAccount {
  id: string
  username: string
  name: string
  role: string
  region: string
  status: string
  lastLogin: string
  unit: string
  device: string
  email: string
  joined: string
}

export interface AuditEntry {
  id: string
  timestamp: string
  actor: string
  role: string
  category: string
  action: string
  detail: string
  target: string
  ipAddress: string
  result: string
}

export interface Personnel {
  id: string
  name: string
  role: string
  region: string
  status: string
  clearance: string
  reportsSubmitted?: number
  kpiScore?: number
}

export interface KpiRegion {
  name: string
  kode: string
  score: number
  reports: number
  responseTime: string
}

function now(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function shortTime(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

let auditSeq = 2000
function nextAuditId(): string {
  auditSeq += 1
  return `LOG-2023-${auditSeq}`
}

let reportSeq = 100
function nextReportId(): string {
  reportSeq += 1
  return `REP-2023-${String(reportSeq).padStart(3, '0')}`
}

let alertSeq = 100
function nextAlertId(): string {
  alertSeq += 1
  return `ALT-${String(alertSeq).padStart(3, '0')}`
}

let userSeq = 100
function nextUserId(): string {
  userSeq += 1
  return `USR-${String(userSeq).padStart(3, '0')}`
}

function currentActor(): { actor: string; role: string } {
  const role = localStorage.getItem('bimcs_role') || 'Super Admin Nasional'
  return { actor: role, role }
}

interface NewReportInput {
  title: string
  type: string
  category: string
  urgency: string
  region: string
  sender: string
  description?: string
  ringkasan?: string
  kronologi?: string
  pihakTerkait?: string
  potensiDampak?: string
  rekomendasiAwal?: string
  unit?: string
  binda?: string
  provinsi?: string
  kabupaten?: string
  kecamatan?: string
  attachments?: { name: string; kind: 'foto' | 'dokumen' }[]
  geotag?: { lat: number; lng: number; label: string }
  offline?: boolean
}

interface NewUserInput {
  name: string
  username: string
  role: string
  region: string
  unit: string
  email: string
}

interface BimcsState {
  reports: Report[]
  alerts: AlertItem[]
  users: UserAccount[]
  personnel: Personnel[]
  auditLog: AuditEntry[]
  kpi: KpiRegion[]
  personalKpi: {
    reports: number
    verified: number
    score: number
    responseTime: string
  }
  kpiHistory: KpiHistoryEntry[]

  // --- audit ---
  addAudit: (
    entry: Omit<AuditEntry, 'id' | 'timestamp'> & { timestamp?: string },
  ) => void

  // --- report workflow ---
  createReport: (input: NewReportInput) => Report
  submitReport: (id: string) => void
  coordinatorForward: (id: string, note?: string) => void
  verifyReport: (id: string, note?: string, threatClass?: string) => void
  rejectReport: (id: string, reason: string) => void
  analyzeReport: (id: string, note?: string) => void
  assessReport: (
    id: string,
    payload: {
      threatClass?: string
      threatLevel?: string
      intelligenceAssessment?: string
      impactScore?: number
    },
  ) => void
  // AI-assisted analyst workbench
  saveAiReport: (
    id: string,
    aiReport: AiIntelReport,
    generated?: boolean,
  ) => void
  approveBinda: (id: string, note?: string) => void
  nationalAccept: (
    id: string,
    payload: {
      executiveSummary: string
      nationalAssessment?: string
      strategicAnalysis?: string
      nationalRiskAssessment?: string
      executiveNotes?: string
    },
  ) => void
  nationalRevise: (id: string, reason: string) => void
  followUpReport: (id: string) => void
  approveReport: (id: string, note?: string) => void
  completeReport: (id: string) => void
  escalateReport: (id: string, reason?: string) => void
  syncOfflineReport: (id: string) => void

  // --- alert workflow ---
  createPanicAlert: (reporter: string, location: string) => AlertItem
  setAlertStatus: (id: string, status: string) => void

  // --- user workflow ---
  createUser: (input: NewUserInput) => UserAccount
  toggleUserStatus: (id: string) => void
  resetPassword: (id: string) => void
  resetMFA: (id: string) => void
  resetDevice: (id: string) => void
}

function seedReportTimeline(r: {
  stage: string
  sender: string
  region: string
}): TimelineEntry[] {
  return [
    {
      stage: 'Draft',
      time: '27 Okt 14:30',
      actor: r.sender,
      note: 'Laporan dibuat oleh personel lapangan dan disimpan sebagai draf awal.',
    },
    {
      stage: 'Dikirim',
      time: '27 Okt 14:42',
      actor: r.sender,
      note: `Laporan dikirim dari ${r.region} ke pusat untuk diproses.`,
    },
  ]
}

export const useStore = create<BimcsState>((set, get) => ({
  reports: seedReports.map((r) => ({
    ...r,
    timeline: seedReportTimeline(r as any),
  })) as Report[],
  alerts: seedAlerts.map((a) => ({
    ...a,
    status: 'Baru',
    timeline: [
      {
        stage: 'Baru',
        time: a.time,
        actor: a.source,
        note: 'Peringatan terdeteksi otomatis oleh sistem dan masuk antrean.',
      },
    ],
  })) as AlertItem[],
  users: [...seedUsers] as UserAccount[],
  personnel: seedPersonnel.map((p) => ({
    ...p,
    reportsSubmitted: Math.floor(Math.random() * 40) + 10,
    kpiScore: Math.floor(Math.random() * 15) + 82,
  })) as Personnel[],
  auditLog: [...seedAudit] as AuditEntry[],
  kpi: [...seedKpi] as KpiRegion[],
  personalKpi: { reports: 24, verified: 19, score: 82, responseTime: '14m' },
  kpiHistory: [],

  addAudit: (entry) =>
    set((s) => ({
      auditLog: [
        {
          id: nextAuditId(),
          timestamp: entry.timestamp ?? now(),
          actor: entry.actor,
          role: entry.role,
          category: entry.category,
          action: entry.action,
          detail: entry.detail,
          target: entry.target,
          ipAddress: entry.ipAddress,
          result: entry.result,
        },
        ...s.auditLog,
      ],
    })),

  createReport: (input) => {
    const { actor, role } = currentActor()
    const id = nextReportId()
    const stage = input.offline ? 'Draft' : 'Draft'
    const report: Report = {
      id,
      title: input.title,
      type: input.type,
      category: input.category,
      urgency: input.urgency,
      sender: input.sender,
      region: input.region,
      time: now(),
      status: 'Belum Diverifikasi',
      stage,
      description: input.description,
      ringkasan: input.ringkasan,
      kronologi: input.kronologi,
      pihakTerkait: input.pihakTerkait,
      potensiDampak: input.potensiDampak,
      rekomendasiAwal: input.rekomendasiAwal,
      unit: input.unit,
      binda: input.binda,
      provinsi: input.provinsi ?? input.region,
      kabupaten: input.kabupaten,
      kecamatan: input.kecamatan,
      verificationStatus: 'Menunggu',
      followUpStatus: 'Belum Ditindaklanjut',
      attachments: input.attachments,
      geotag: input.geotag,
      offline: input.offline,
      timeline: [
        {
          stage: 'Draft',
          time: shortTime(),
          actor: input.sender,
          note: input.offline
            ? 'Draf dibuat dalam mode luring dan menunggu sinkronisasi.'
            : 'Laporan dibuat dan disimpan sebagai draf.',
        },
      ],
    }
    set((s) => ({ reports: [report, ...s.reports] }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Buat Laporan',
      detail: `Membuat draf laporan: ${input.title}`,
      target: id,
      ipAddress: '114.125.22.10',
      result: 'Berhasil',
    })
    return report
  },

  submitReport: (id) => {
    const { actor, role } = currentActor()
    set((s) => {
      return {
        reports: s.reports.map((r) =>
          r.id === id
            ? {
                ...r,
                stage: 'Dikirim',
                status: 'Belum Diverifikasi',
                offline: false,
                timeline: [
                  ...(r.timeline ?? []),
                  {
                    stage: 'Dikirim',
                    time: shortTime(),
                    actor: r.sender,
                    note: `Laporan dikirim dari ${r.region} ke pusat untuk diproses.`,
                  },
                ],
              }
            : r,
        ),
        personalKpi: {
          ...s.personalKpi,
          reports: s.personalKpi.reports + 1,
        },
      }
    })
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Kirim Laporan',
      detail: `Mengirim laporan ${id} untuk verifikasi`,
      target: id,
      ipAddress: '114.125.22.10',
      result: 'Berhasil',
    })
  },

  coordinatorForward: (id, note) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Review Koordinator',
              status: 'Review Koordinator',
              coordinatorNote: note ?? r.coordinatorNote,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Review Koordinator',
                  time: shortTime(),
                  actor,
                  note:
                    note ||
                    'Koordinator wilayah meninjau laporan dan meneruskannya ke analis.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Review Koordinator',
      detail: `Koordinator meneruskan laporan ${id} ke analis`,
      target: id,
      ipAddress: '10.0.4.18',
      result: 'Berhasil',
    })
  },

  verifyReport: (id, note, threatClass) => {
    const { actor, role } = currentActor()
    set((s) => {
      return {
        reports: s.reports.map((r) =>
          r.id === id
            ? {
                ...r,
                stage: 'Diverifikasi',
                status: 'Diverifikasi',
                verificationStatus: 'Terverifikasi',
                analysisNote: note ?? r.analysisNote,
                threatClass: threatClass ?? r.threatClass,
                timeline: [
                  ...(r.timeline ?? []),
                  {
                    stage: 'Diverifikasi',
                    time: shortTime(),
                    actor,
                    note:
                      note ||
                      'Keaslian sumber dan kelengkapan data diverifikasi oleh analis.',
                  },
                ],
              }
            : r,
        ),
        personalKpi: {
          ...s.personalKpi,
          verified: s.personalKpi.verified + 1,
        },
      }
    })
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Verifikasi Laporan',
      detail: `Memverifikasi laporan ${id}${threatClass ? ` (klasifikasi: ${threatClass})` : ''}`,
      target: id,
      ipAddress: '10.0.5.22',
      result: 'Berhasil',
    })
  },

  rejectReport: (id, reason) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Ditolak',
              status: 'Ditolak',
              rejectReason: reason,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Ditolak',
                  time: shortTime(),
                  actor,
                  note: `Laporan ditolak: ${reason}`,
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Tolak Laporan',
      detail: `Menolak laporan ${id}: ${reason}`,
      target: id,
      ipAddress: '10.0.5.22',
      result: 'Peringatan',
    })
  },

  analyzeReport: (id, note) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Analisis',
              status: 'Dianalisis',
              analysisNote: note ?? r.analysisNote,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Analisis',
                  time: shortTime(),
                  actor,
                  note:
                    note ||
                    'Konteks ancaman dianalisis dan dikaitkan dengan laporan terkait.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Analisis Laporan',
      detail: `Menambahkan analisis pada laporan ${id}`,
      target: id,
      ipAddress: '10.0.5.22',
      result: 'Berhasil',
    })
  },

  assessReport: (id, payload) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Analisis',
              status: 'Dianalisis',
              threatClass: payload.threatClass ?? r.threatClass,
              threatLevel: payload.threatLevel ?? r.threatLevel,
              intelligenceAssessment:
                payload.intelligenceAssessment ?? r.intelligenceAssessment,
              impactScore: payload.impactScore ?? r.impactScore,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Analisis',
                  time: shortTime(),
                  actor,
                  note: `Penilaian intelijen ditambahkan${
                    payload.threatClass
                      ? ` — klasifikasi: ${payload.threatClass}`
                      : ''
                  }${payload.threatLevel ? `, tingkat ancaman: ${payload.threatLevel}` : ''}.`,
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Penilaian Intelijen',
      detail: `Menilai laporan ${id} (${payload.threatClass ?? '-'} / ${payload.threatLevel ?? '-'})`,
      target: id,
      ipAddress: '10.0.5.22',
      result: 'Berhasil',
    })
  },

  saveAiReport: (id, aiReport, generated) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              aiReport,
              aiGenerated: generated ?? r.aiGenerated ?? true,
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: generated
        ? 'Generasi Laporan Intelijen (AI)'
        : 'Edit Laporan Intelijen',
      detail: generated
        ? `AI membuat draf laporan intelijen untuk ${id} (menunggu validasi analis)`
        : `Analis menyunting laporan intelijen ${id}`,
      target: id,
      ipAddress: '10.0.5.22',
      result: 'Berhasil',
    })
  },

  approveBinda: (id, note) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Disetujui BINDA',
              status: 'Disetujui BINDA',
              bindaApprovalNote: note ?? r.bindaApprovalNote,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Disetujui BINDA',
                  time: shortTime(),
                  actor,
                  note:
                    note ||
                    'Kepala BINDA menyetujui laporan intelijen dan meneruskannya ke tingkat Nasional.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Persetujuan Kepala BINDA',
      detail: `Menyetujui laporan intelijen ${id}`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Berhasil',
    })
  },

  nationalAccept: (id, payload) => {
    const { actor, role } = currentActor()
    const report = get().reports.find((r) => r.id === id)
    set((s) => {
      const from = s.personalKpi.score
      const to = Math.min(100, from + 6)
      return {
        reports: s.reports.map((r) =>
          r.id === id
            ? {
                ...r,
                stage: 'Diterima Nasional',
                status: 'Diterima Nasional',
                nationalReviewed: true,
                escalatedNational: true,
                executiveSummary: payload.executiveSummary,
                nationalAssessment: payload.nationalAssessment,
                strategicAnalysis: payload.strategicAnalysis,
                nationalRiskAssessment: payload.nationalRiskAssessment,
                executiveNotes: payload.executiveNotes,
                timeline: [
                  ...(r.timeline ?? []),
                  {
                    stage: 'Diterima Nasional',
                    time: shortTime(),
                    actor,
                    note: 'Analis Nasional menerima laporan sebagai Produk Intelijen Resmi.',
                  },
                ],
              }
            : r,
        ),
        personalKpi: {
          ...s.personalKpi,
          score: to,
        },
        kpiHistory: [
          {
            time: shortTime(),
            from,
            to,
            delta: to - from,
            reason: 'Produk Intelijen Diterima Nasional',
            target: id,
          },
          ...s.kpiHistory,
        ],
      }
    })

    // Auto Early Warning: a high/critical threat level immediately spawns an alert
    const assessLevel = report?.threatLevel || report?.aiReport?.classification
    if (
      assessLevel === 'Tinggi' ||
      assessLevel === 'Kritis' ||
      assessLevel === 'RAHASIA' ||
      assessLevel === 'SANGAT RAHASIA'
    ) {
      const alertId = nextAlertId()
      const alert: AlertItem = {
        id: alertId,
        type:
          assessLevel === 'Kritis' || assessLevel === 'SANGAT RAHASIA'
            ? 'KRITIS'
            : 'TINGGI',
        source: 'PERINGATAN DINI',
        location: report?.provinsi || report?.region || 'Nasional',
        time: shortTime(),
        desc: `[${report?.threatClass || 'Ancaman'}] ${report?.title || 'Laporan intelijen'} — ${report?.binda || report?.region || 'BINDA'} (${id})`,
        status: 'Baru',
        reporter: report?.sender,
        timeline: [
          {
            stage: 'Baru',
            time: shortTime(),
            actor,
            note: `Peringatan dini otomatis dibuat dari produk intelijen resmi ${id}.`,
          },
        ],
      }
      set((s) => ({ alerts: [alert, ...s.alerts] }))
      get().addAudit({
        actor,
        role,
        category: 'Aktivitas Darurat',
        action: 'Peringatan Dini Otomatis',
        detail: `Peringatan dini dibuat otomatis dari laporan resmi ${id}`,
        target: alertId,
        ipAddress: '10.0.5.22',
        result: 'Berhasil',
      })
    }

    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Penerimaan Nasional',
      detail: `Laporan ${id} diterima sebagai Produk Intelijen Resmi`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Berhasil',
    })
  },

  nationalRevise: (id, reason) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Perlu Revisi',
              status: 'Perlu Revisi',
              revisionReason: reason,
              nationalReviewed: false,
              escalatedNational: false,
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Perlu Revisi',
                  time: shortTime(),
                  actor,
                  note: `Laporan dikembalikan ke BINDA untuk revisi: ${reason}`,
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Revisi Nasional',
      detail: `Mengembalikan laporan ${id} untuk revisi: ${reason}`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Peringatan',
    })
  },

  approveReport: (id, note) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Tindak Lanjut',
              status: 'Ditindaklanjuti',
              followUpStatus: 'Proses',
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Tindak Lanjut',
                  time: shortTime(),
                  actor,
                  note:
                    note ||
                    'Tindak lanjut disetujui oleh pimpinan BINDA dan diteruskan ke unit operasi.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Setujui Tindak Lanjut',
      detail: `Menyetujui tindak lanjut laporan ${id}`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Berhasil',
    })
  },

  followUpReport: (id) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Tindak Lanjut',
              status: 'Ditindaklanjuti',
              followUpStatus: 'Proses',
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Tindak Lanjut',
                  time: shortTime(),
                  actor,
                  note: 'Instruksi penanganan diterbitkan ke unit operasi terkait.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Tindak Lanjut',
      detail: `Menyetujui tindak lanjut laporan ${id}`,
      target: id,
      ipAddress: '10.0.8.100',
      result: 'Berhasil',
    })
  },

  completeReport: (id) => {
    const { actor, role } = currentActor()
    set((s) => ({
      reports: s.reports.map((r) =>
        r.id === id
          ? {
              ...r,
              stage: 'Selesai',
              status: 'Selesai',
              followUpStatus: 'Selesai',
              timeline: [
                ...(r.timeline ?? []),
                {
                  stage: 'Selesai',
                  time: shortTime(),
                  actor,
                  note: 'Penanganan rampung dan laporan ditutup dengan arsip resmi.',
                },
              ],
            }
          : r,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Perubahan Laporan',
      action: 'Tandai Selesai',
      detail: `Menutup laporan ${id}`,
      target: id,
      ipAddress: '10.0.8.100',
      result: 'Berhasil',
    })
  },

  escalateReport: (id, reason) => {
    const { actor, role } = currentActor()
    const report = get().reports.find((r) => r.id === id)
    set((s) => {
      return {
        reports: s.reports.map((r) =>
          r.id === id
            ? {
                ...r,
                urgency: 'Kritis',
                escalatedNational: true,
                rekomendasiEskalasi: reason ?? r.rekomendasiEskalasi,
                timeline: [
                  ...(r.timeline ?? []),
                  {
                    stage: r.stage,
                    time: shortTime(),
                    actor,
                    note: `Dieskalasi ke tingkat nasional${reason ? `: ${reason}` : ''}.`,
                  },
                ],
              }
            : r,
        ),
      }
    })

    if (report && isOfficialProduct(report)) {
      // Escalation spawns a national-level critical alert only if it's an official product.
      const id2 = nextAlertId()
      const alert: AlertItem = {
        id: id2,
        type: 'KRITIS',
        source: 'ESKALASI',
        location: report?.region || 'Nasional',
        time: shortTime(),
        desc: `Eskalasi laporan ${id}: ${reason || report?.title || 'ancaman tingkat tinggi'}`,
        status: 'Baru',
        timeline: [
          {
            stage: 'Baru',
            time: shortTime(),
            actor,
            note: `Dieskalasi dari laporan ${id} ke Pusat Komando Nasional.`,
          },
        ],
      }
      set((s) => ({ alerts: [alert, ...s.alerts] }))
    }
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Eskalasi Laporan',
      detail: `Eskalasi laporan ${id} ke tingkat nasional`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Berhasil',
    })
  },

  syncOfflineReport: (id) => {
    // Bringing an offline draft online == submitting it.
    get().submitReport(id)
    const { actor, role } = currentActor()
    get().addAudit({
      actor,
      role,
      category: 'Sinkronisasi',
      action: 'Sinkronkan Draf',
      detail: `Sinkronisasi draf luring ${id} ke server pusat`,
      target: id,
      ipAddress: '114.125.22.10',
      result: 'Berhasil',
    })
  },

  createPanicAlert: (reporter, location) => {
    const { actor, role } = currentActor()
    const id = nextAlertId()
    const alert: AlertItem = {
      id,
      type: 'KRITIS',
      source: 'PANIC',
      location,
      time: shortTime(),
      desc: `TOMBOL PANIK DIAKTIFKAN oleh ${reporter} di ${location}. Bantuan segera diperlukan.`,
      status: 'Baru',
      isPanic: true,
      reporter,
      timeline: [
        {
          stage: 'Baru',
          time: shortTime(),
          actor: reporter,
          note: 'Sinyal panik dikirim dari perangkat personel lapangan.',
        },
      ],
    }
    set((s) => ({ alerts: [alert, ...s.alerts] }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Darurat',
      action: 'Aktifkan Panic Button',
      detail: `Sinyal panik dari ${reporter} (${location})`,
      target: id,
      ipAddress: '114.125.22.10',
      result: 'Berhasil',
    })
    return alert
  },

  setAlertStatus: (id, status) => {
    const { actor, role } = currentActor()
    set((s) => ({
      alerts: s.alerts.map((a) =>
        a.id === id
          ? {
              ...a,
              status,
              timeline: [
                ...(a.timeline ?? []),
                {
                  stage: status,
                  time: shortTime(),
                  actor,
                  note: `Status peringatan diperbarui menjadi ${status}.`,
                },
              ],
            }
          : a,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Aktivitas Verifikasi',
      action: 'Ubah Status Peringatan',
      detail: `Peringatan ${id} → ${status}`,
      target: id,
      ipAddress: '10.0.2.15',
      result: 'Berhasil',
    })
  },

  createUser: (input) => {
    const { actor, role } = currentActor()
    const id = nextUserId()
    const user: UserAccount = {
      id,
      username: input.username,
      name: input.name,
      role: input.role,
      region: input.region,
      unit: input.unit,
      email: input.email,
      status: 'Aktif',
      lastLogin: '-',
      device: 'Belum Terdaftar',
      joined: now().split(' ')[0],
    }
    set((s) => ({ users: [user, ...s.users] }))
    get().addAudit({
      actor,
      role,
      category: 'Manajemen Pengguna',
      action: 'Buat Pengguna',
      detail: `Membuat akun ${input.username} (${input.role})`,
      target: id,
      ipAddress: '192.168.2.50',
      result: 'Berhasil',
    })
    return user
  },

  toggleUserStatus: (id) => {
    const { actor, role } = currentActor()
    let newStatus = ''
    set((s) => ({
      users: s.users.map((u) => {
        if (u.id !== id) return u
        newStatus = u.status === 'Aktif' ? 'Tidak Aktif' : 'Aktif'
        return { ...u, status: newStatus }
      }),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Manajemen Pengguna',
      action:
        newStatus === 'Aktif' ? 'Aktifkan Pengguna' : 'Nonaktifkan Pengguna',
      detail: `Status pengguna ${id} → ${newStatus}`,
      target: id,
      ipAddress: '192.168.2.50',
      result: 'Berhasil',
    })
  },

  resetPassword: (id) => {
    const { actor, role } = currentActor()
    get().addAudit({
      actor,
      role,
      category: 'Manajemen Pengguna',
      action: 'Reset Password',
      detail: `Reset kata sandi untuk pengguna ${id}`,
      target: id,
      ipAddress: '192.168.2.50',
      result: 'Berhasil',
    })
  },

  resetMFA: (id) => {
    const { actor, role } = currentActor()
    get().addAudit({
      actor,
      role,
      category: 'Manajemen Pengguna',
      action: 'Reset MFA',
      detail: `Reset MFA untuk pengguna ${id}`,
      target: id,
      ipAddress: '192.168.2.50',
      result: 'Berhasil',
    })
  },

  resetDevice: (id) => {
    const { actor, role } = currentActor()
    set((s) => ({
      users: s.users.map((u) =>
        u.id === id ? { ...u, device: 'Belum Terdaftar' } : u,
      ),
    }))
    get().addAudit({
      actor,
      role,
      category: 'Manajemen Pengguna',
      action: 'Reset Device',
      detail: `Reset perangkat untuk pengguna ${id}`,
      target: id,
      ipAddress: '192.168.2.50',
      result: 'Berhasil',
    })
  },
}))
